import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { TreePine, Loader2, ShieldCheck } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1609260840244-22b52bfc6664?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export function InviteAcceptView({ token }) {
  const { loginWithToken } = useAuth();
  const [state, setState] = useState("loading"); // loading | ok | invalid
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.inviteInfo(token)
      .then((d) => { setInfo(d); setUsername(""); setState("ok"); })
      .catch((e) => { setError(e?.response?.data?.detail || "This invite link is not valid"); setState("invalid"); });
  }, [token]);

  const goHome = () => { window.history.replaceState({}, "", "/"); window.location.reload(); };

  const submit = async (e) => {
    e?.preventDefault();
    if (!username.trim() || !password) return toast.error("Pick a username and password");
    if (password.length < 4) return toast.error("Password is too short");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const { token: jwt, user } = await api.acceptInvite(token, username.trim(), password, email.trim(), window.location.origin);
      loginWithToken(jwt, user);
      toast.success(`Welcome, ${user.name || user.username}!`);
      window.history.replaceState({}, "", "/");
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create your account");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F6F8] p-6">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-3xl border border-[#D8E0E8] bg-white shadow-sm md:grid-cols-2">
        <div className="flex flex-col justify-center p-8 sm:p-12" data-testid="invite-accept-view">
          <div className="mb-4 flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="" className="h-10 w-10 object-contain" />
            <div className="font-brand text-lg text-[#082D4A]">Where Am i From<span className="text-[#D4A017]">.net</span></div>
          </div>

          {state === "loading" && (
            <div className="flex items-center gap-2 text-[#64748B]" data-testid="invite-loading">
              <Loader2 size={18} className="animate-spin" /> Checking your invite…
            </div>
          )}

          {state === "invalid" && (
            <div data-testid="invite-invalid">
              <h1 className="font-serif text-3xl font-bold text-[#082D4A]">Invite unavailable</h1>
              <p className="mt-3 text-[#475569]">{error}</p>
              <Button onClick={goHome} data-testid="invite-go-home" className="mt-6 rounded-full bg-[#082D4A] text-white hover:bg-[#08223A]">Go to Where Am I From</Button>
            </div>
          )}

          {state === "ok" && (
            <>
              <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#E8EEF3] px-3 py-1 text-xs font-medium uppercase tracking-wider text-[#082D4A]">
                <ShieldCheck size={13} /> You're invited{info?.role === "admin" ? " as an admin" : ""}
              </span>
              <h1 className="font-serif text-3xl font-bold leading-tight text-[#082D4A] sm:text-4xl">
                {info?.name ? `Welcome, ${info.name}` : (info?.family_name ? `Join ${info.family_name}` : "Join the family")}
              </h1>
              <p className="mt-3 text-[#475569]">Create your account to add and correct family information.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label>Choose a username</Label>
                  <Input data-testid="invite-username-input" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
                </div>
                <div className="space-y-1.5">
                  <Label>Create a password</Label>
                  <Input type="password" data-testid="invite-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Confirm password</Label>
                  <Input type="password" data-testid="invite-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Email <span className="font-normal text-[#94A3B8]">(optional — we'll email your tree link)</span></Label>
                  <Input type="email" data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                </div>
                <Button type="submit" disabled={busy} data-testid="invite-submit-button" className="w-full rounded-full bg-[#D4A017] py-6 text-base text-white hover:bg-[#B0850F]">
                  {busy ? "Creating account…" : "Create account & sign in"}
                </Button>
              </form>
            </>
          )}
        </div>
        <div className="relative hidden min-h-[300px] md:block">
          <img src={HERO} alt="Family" className="absolute inset-0 h-full w-full object-cover" />
        </div>
      </div>
    </div>
  );
}
